/**
 * The seller portal's sign-in chrome, shared by every page that renders it.
 *
 * These pages are deliberately not styled with the app's Tailwind + @terra/ui
 * setup: they are the only screens a signed-out seller sees, and they reset the
 * global `* { border-color }` and body styles rather than inherit them. Keeping
 * the block in one module means the sign-in and password-reset screens cannot
 * drift apart — they are the same card, two steps apart in the same flow.
 */
/* ── Scoped CSS that overrides Tailwind base / @terra/ui globals ── */
export const AUTH_SCOPED_CSS = `
@keyframes ts-login-spin {
  to { transform: rotate(360deg); }
}

/* Reset the global * { border-color } and body styles inside our login scope */
.ts-login-page,
.ts-login-page *,
.ts-login-page *::before,
.ts-login-page *::after {
  box-sizing: border-box;
  border-color: transparent;
}

.ts-login-page {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 100vh;
  background-color: #efefef !important;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  margin: 0;
  padding: 0;
}

.ts-login-page--classic {
  background-color: transparent !important;
}

.ts-login-modern-container {
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  max-width: 960px;
  width: 100%;
  min-height: 620px;
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  margin: auto;
  box-shadow: 0 20px 40px rgba(0,0,0,0.06);
}

.ts-login-side-image {
  display: block;
  width: 100%;
  height: 100%;
}

.ts-login-side-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@media (max-width: 860px) {
  .ts-login-modern-container {
    grid-template-columns: 1fr;
    max-width: 428px;
  }
  .ts-login-side-image {
    display: none;
  }
}

.ts-login-card {
  width: 428px;
  min-height: 620px;
  background-color: #ffffff;
  border: none !important;
  border-radius: 0 !important;
  padding: 48px 44px 40px 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: none !important;
}

.ts-login-logo {
  width: 148px;
  margin-bottom: 20px;
  display: block;
}

.ts-login-heading {
  font-size: 17px;
  font-weight: 700;
  color: #e53935;
  text-transform: uppercase;
  text-align: center;
  margin: 0 0 6px 0;
  letter-spacing: 0.7px;
  line-height: 1.35;
  font-style: normal !important;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
}

.ts-login-subtext {
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  margin: 0 0 24px 0;
  line-height: 1.55;
  font-weight: 400;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
}

.ts-login-form {
  width: 100%;
}

.ts-login-field {
  margin-bottom: 18px;
}

.ts-login-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 7px;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.65px;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
}

.ts-login-input {
  width: 100%;
  height: 44px;
  border: 1.5px solid #e5e7eb !important;
  border-radius: 7px !important;
  padding: 0 14px;
  background-color: #fafafa !important;
  font-size: 14.5px;
  line-height: 1;
  outline: none !important;
  box-shadow: none !important;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  color: #111111;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
.ts-login-input::placeholder {
  color: #b0b7c3;
  font-size: 13.5px;
}
.ts-login-input:focus {
  border-color: #e53935 !important;
  background-color: #ffffff !important;
  outline: none !important;
  box-shadow: none !important;
}

.ts-login-pw-wrap {
  position: relative;
}

.ts-login-pw-toggle {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #111827;
  display: flex;
  align-items: center;
  background: none !important;
  border: none !important;
  cursor: pointer;
  padding: 0;
  font-size: 20px;
  outline: none !important;
  box-shadow: none !important;
}

.ts-login-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.ts-login-checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 8px;
}

.ts-login-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #e53935;
  border-radius: 0 !important;
  border: 1px solid #dddddd !important;
  cursor: pointer;
  margin: 0;
  -webkit-appearance: auto;
  appearance: auto;
}

.ts-login-remember {
  font-size: 15px;
  font-weight: 500;
  color: #111827;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
}

.ts-login-forgot {
  font-size: 15px;
  font-weight: 500;
  color: #111827;
  text-decoration: none;
  background: none !important;
  border: none !important;
  cursor: pointer;
  padding: 0;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  outline: none !important;
}
.ts-login-forgot:hover {
  text-decoration: underline;
}

/* ── Password reset panel ───────────────────────────────────────────────── */
.ts-reset-channels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.ts-reset-channel {
  padding: 10px 18px;
  border: 1.5px solid #e5e7eb;
  border-radius: 50px;
  background: #fff;
  font-size: 13.5px;
  font-weight: 600;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  white-space: nowrap;
}
.ts-reset-channel:hover {
  border-color: #d1d5db;
  background: #f9fafb;
  color: #6b7280;
}
.ts-reset-channel.is-active {
  border: 1.5px solid #e53935;
  background: #fff;
  color: #e53935;
}
.ts-reset-hint {
  margin: 6px 0 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: #6b7280;
}
.ts-reset-sent {
  margin: 0 0 4px;
  padding: 11px 13px;
  border-radius: 8px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  font-size: 13.5px;
  line-height: 1.5;
  color: #166534;
}
.ts-reset-sent strong {
  font-weight: 700;
}
/* Wide tracking makes a 6-digit code easy to read back off a phone. */
.ts-reset-code {
  letter-spacing: 9px;
  font-size: 19px;
  font-weight: 700;
  text-align: center;
}
.ts-reset-pw-toggle {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}
.ts-reset-back {
  margin-top: 2px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  color: #e0393e;
  cursor: pointer;
  padding: 6px;
}
.ts-reset-back:hover:not(:disabled) {
  text-decoration: underline;
}
.ts-reset-back:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}


.ts-login-submit {
  width: 100%;
  height: 46px;
  background-color: #e53935 !important;
  color: #ffffff !important;
  font-weight: 700;
  font-size: 13.5px;
  border-radius: 7px !important;
  border: none !important;
  cursor: pointer;
  margin-bottom: 20px;
  transition: background-color 0.2s;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  letter-spacing: 1px;
  line-height: 1;
  text-transform: uppercase;
  outline: none !important;
  box-shadow: none !important;
  -webkit-appearance: none;
  appearance: none;
}
.ts-login-submit:hover {
  background-color: #d32f2f !important;
}
.ts-login-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ts-login-back {
  font-size: 13px;
  font-weight: 500;
  color: #e53935;
  text-align: center;
  display: block;
  text-decoration: none !important;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
  line-height: 1.4;
  opacity: 0.85;
  cursor: pointer;
}
.ts-login-back:hover {
  opacity: 1;
  text-decoration: underline !important;
}

.ts-login-error {
  font-size: 13px;
  color: #d32f2f;
  margin: 6px 0 12px 0;
  line-height: 18px;
  font-family: 'Arial', 'Helvetica', sans-serif !important;
}

.ts-login-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #ffffff;
  border-radius: 50% !important;
  animation: ts-login-spin 0.6s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
}
`;
